import { cloneDeep, isEqual, sortBy } from 'lodash';
import { DateTime } from 'luxon';
import { computed, onBeforeMount, provide, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';

import { crossbeamApi } from '@/api';
import {
  EMPTY_MAPPING,
  MAP_TRAY_TO_INTEGRATION_PROFILE_TYPE,
} from '@/constants/integrations';
import { captureException } from '@/errors';
import { ProvidedTrayState } from '@/injectionKeys/integration_profiles';
import {
  useFlashesStore,
  useIntegrationProfileStore,
  usePartnerCloudStore,
  usePartnersStore,
} from '@/stores';
import {
  EcosystemCrossbeamField,
  EcosystemFieldMappings,
  EcosystemFieldsPush,
  PartnerAttributeFieldMappings,
  PartnerFieldMapping,
  PartnerOption,
  PutPartnerFieldMappingsPayload,
  SharedPartnerField,
  TrayPayload,
} from '@/types/integration_profiles';
import {
  Mapping,
  TabValue,
  TrayExternalId,
  TrayTag,
} from '@/types/integrations';

export default function useTrayConfig() {
  const partnerCloudStore = usePartnerCloudStore();
  const route = useRoute();

  const { addErrorFlash, addSuccessFlash } = useFlashesStore();

  const pathTrayTag = computed(() => route.params.tray_tag as TrayTag);

  const enabledTray = computed(() => {
    const foundTray = partnerCloudStore.enabledTray.find(
      (trayIntegration) => trayIntegration?.trayTag === pathTrayTag.value,
    );
    return foundTray;
  });

  const isIntegrationEnabled = ref(enabledTray?.value?.enabled ?? false);

  function parseConfigValue<T = boolean | EcosystemFieldsPush>(
    externalId: TrayExternalId,
    defaultValue: T,
  ): T {
    const raw = enabledTray.value?.configValues?.find(
      (c) => c.externalId === externalId,
    )?.value;
    if (!raw) return defaultValue;
    try {
      const parsed = JSON.parse(raw);
      return parsed;
    } catch {
      return defaultValue;
    }
  }

  const integrationProfileType = computed(() => {
    if (
      pathTrayTag.value &&
      pathTrayTag.value in MAP_TRAY_TO_INTEGRATION_PROFILE_TYPE
    ) {
      const value = MAP_TRAY_TO_INTEGRATION_PROFILE_TYPE[pathTrayTag.value];
      if (!value) {
        throw Error('Integration profile type not found');
      }
      return value;
    }

    return undefined;
  });

  const friendlyNameMap = {
    salesforce: 'Salesforce',
    hubspot: 'HubSpot',
    clari: 'Clari',
    microsoft_dynamics: 'Microsoft Dynamics',
    crossbeam_copilot: 'Crossbeam Copilot',
    snowflake: 'Snowflake',
    databricks: 'Databricks',
  } as const;

  const friendlyName = computed(() => {
    if (
      !integrationProfileType.value ||
      integrationProfileType.value === 'webhook' ||
      integrationProfileType.value === 'api_filter'
    )
      return '';
    return friendlyNameMap[integrationProfileType.value];
  });

  const rawInitialState = {
    isEnabled: enabledTray.value?.enabled ?? false,
    oppPush: parseConfigValue<boolean>('external_opps-enabled', false),
    customObjectPush: parseConfigValue<boolean>(
      'external_push_overlaps',
      false,
    ),
    partnerFieldMappingsPush: parseConfigValue<boolean>(
      'external_push_partner_field_mappings',
      false,
    ),
    partnerAttributesPush: parseConfigValue<boolean>(
      'external_push_partner_attributes',
      false,
    ),
    accountPush: parseConfigValue<EcosystemFieldsPush>(
      'external_push_ecosystem_fields',
      'NONE',
    ),
    ecosystemMaps: parseConfigValue<EcosystemFieldMappings>(
      'external_ecosystem_fields',
      null,
    ),
    partnerAttributes: parseConfigValue<PartnerAttributeFieldMappings>(
      'external_partner_attributes',
      [],
    ),
    profileId: parseConfigValue<string | undefined>(
      'external_crossbeam-profile-id',
      undefined,
    ),
    partnerFieldMappings: [EMPTY_MAPPING] as Mapping[],
    sharedPartnerFields: [] as PartnerOption[],
    isProfileSettingDirty: false,
  };

  // Deep‑clone rawInitialState so Vue’s reactivity doesn’t share any nested references.
  // That way, when we diff mappings later, we’re comparing truly independent objects.
  const initialState = reactive(cloneDeep(rawInitialState));

  // Same deal for the “live” state – start with a fresh clone so all nested properties
  // can be tracked and diffed without accidental shared references.
  const state = reactive({
    ...cloneDeep(rawInitialState),
    isSaving: false,
    isDeleting: false,
    isSyncing: false,
    profileType: integrationProfileType.value,
    saveTrayConfig,
    savePartnerFieldMappings,
    resetState,
    saveTabSettings,
    friendlyName: friendlyName.value,
    enabledTray,
    hasTrayConfigChanges: false,
    isAccountPushConfigValid: true,
    isPartnerFieldMappingsDirty: false,
    isProfileSettingDirty: false,
  });

  // filter and trim the partner field mappings to make it easier to compare
  function filterAndTrimMappings(mappings: Mapping[]) {
    return mappings
      .filter(
        (m) => m.partner_source_field_id !== null && m.source_field_id !== null,
      )
      .map((m) => ({
        partner_source_field_id: m.partner_source_field_id,
        source_field_id: m.source_field_id,
      }));
  }

  const filteredInitialMappings = computed(() =>
    filterAndTrimMappings(initialState.partnerFieldMappings),
  );

  const filteredCurrentMappings = computed(() =>
    filterAndTrimMappings(state.partnerFieldMappings),
  );

  const isPartnerFieldMappingsDirty = computed(
    () =>
      !isEqual(filteredCurrentMappings.value, filteredInitialMappings.value),
  );

  // AccountObjectFields always emits a row per population, with crm_field
  // undefined when unmapped, while Tray only stores the mapped rows. Compare
  // and send the mapped rows so a single mapped field is a complete config.
  function mappedEcosystemFields(maps: EcosystemFieldMappings) {
    return sortBy(
      (maps ?? []).filter(
        (
          m,
        ): m is {
          crossbeam_field: EcosystemCrossbeamField;
          crm_field: string;
        } => Boolean(m.crm_field),
      ),
      'crossbeam_field',
    );
  }

  const newIsTrayConfigUpdateRequired = computed(() => {
    if (partnerCloudStore.inProgressInstallation) return true;
    if (state.isEnabled !== initialState.isEnabled) return true;
    if (state.oppPush !== initialState.oppPush) return true;
    if (state.customObjectPush !== initialState.customObjectPush) return true;
    if (
      state.partnerFieldMappingsPush !== initialState.partnerFieldMappingsPush
    ) {
      return true;
    }
    if (state.accountPush !== initialState.accountPush) return true;

    if (
      !isEqual(
        mappedEcosystemFields(state.ecosystemMaps),
        mappedEcosystemFields(initialState.ecosystemMaps),
      )
    ) {
      return true;
    }
    if (state.partnerAttributesPush !== initialState.partnerAttributesPush) {
      return true;
    }
    if (!isEqual(state.partnerAttributes, initialState.partnerAttributes)) {
      return true;
    }
    return false;
  });

  // With the push on and nothing mapped, Tray runs and pushes nothing while the
  // status still reads Active. Block the save until a field is mapped or the
  // push is turned off.
  const isAccountPushConfigValid = computed(
    () =>
      integrationProfileType.value !== 'salesforce' ||
      state.accountPush !== 'ALL' ||
      mappedEcosystemFields(state.ecosystemMaps).length > 0,
  );

  // Assign computed property after both are declared
  Object.defineProperty(state, 'hasTrayConfigChanges', {
    get: () => newIsTrayConfigUpdateRequired.value,
  });
  Object.defineProperty(state, 'isAccountPushConfigValid', {
    get: () => isAccountPushConfigValid.value,
  });
  Object.defineProperty(state, 'isPartnerFieldMappingsDirty', {
    get: () => isPartnerFieldMappingsDirty.value,
  });

  function resetState() {
    const newData = cloneDeep(initialState);
    Object.assign(state, newData);
  }

  provide(ProvidedTrayState, state);

  const integrationProfileId = computed(() => {
    if (!integrationProfileType.value) return null;

    return integrationProfileStore.getIntegrationProfileByType(
      integrationProfileType.value,
    )?.profile_id;
  });

  const integrationProfileStore = useIntegrationProfileStore();
  const createTrayConfigPayload = (): TrayPayload => {
    const profileId = integrationProfileId.value;

    if (!profileId) {
      throw new Error('Integration profile settings not found');
    }

    if (!enabledTray?.value) {
      throw new Error('Tray integration info not found');
    }

    const config: TrayPayload['config'] = {
      integration_profile_id: profileId,
      start_timestamp: DateTime.now().plus({ minutes: 15 }).toISO(),
      enabled: state.isEnabled,
      opps_enabled: state.oppPush,
      push_overlaps: state.customObjectPush,
      push_ecosystem_fields: state.accountPush,
      push_partner_field_mappings: state.partnerFieldMappingsPush,
    };

    if (integrationProfileType.value === 'salesforce') {
      config.ecosystem_fields = mappedEcosystemFields(state.ecosystemMaps);
      config.push_partner_attributes = state.partnerAttributesPush;
      config.partner_attributes = state.partnerAttributes.filter(
        (m): m is { crossbeam_field: 'tags'; crm_field: string } =>
          Boolean(m.crm_field),
      );
    }

    return {
      config,
      instance_name: enabledTray.value.name,
      instance_id: enabledTray.value.id,
    };
  };

  const updateTrayInstanceConfig = async (payload: TrayPayload) => {
    const { error } = await crossbeamApi.PUT('/v0.1/tray-integrations', {
      body: payload,
    });
    if (error) throw error;
  };

  async function savePartnerFieldMappings() {
    if (!integrationProfileId.value) return;

    const payload = state.partnerFieldMappings
      .filter(
        (m) => m.source_field_id !== null && m.partner_source_field_id !== null,
      )
      .map((m) => ({
        source_field_id: m.source_field_id,
        partner_source_field_id: m.partner_source_field_id,
        partner_organization_uuid: m.partner_organization_uuid,
      })) as PutPartnerFieldMappingsPayload;

    const { data } = await crossbeamApi.PUT(
      '/v0.1/integration-profiles/{profile_id}/partner-field-mappings',
      {
        body: payload,
        params: {
          path: {
            profile_id: integrationProfileId.value,
          },
        },
      },
    );
    setPartnerFieldMappings(data?.items ?? []);
  }

  function setPartnerFieldMappings(data: PartnerFieldMapping[]) {
    const mappingsWithEmpty = data.map((m) => ({
      ...m,
      empty: false,
    }));
    initialState.partnerFieldMappings = cloneDeep(mappingsWithEmpty);
    state.partnerFieldMappings = cloneDeep(mappingsWithEmpty);
  }

  function syncInitialStateFromCurrent() {
    initialState.partnerFieldMappingsPush = state.partnerFieldMappingsPush;
    initialState.partnerAttributesPush = state.partnerAttributesPush;
    initialState.isEnabled = state.isEnabled;
    initialState.oppPush = state.oppPush;
    initialState.customObjectPush = state.customObjectPush;
    initialState.accountPush = state.accountPush;
    initialState.ecosystemMaps = state.ecosystemMaps;
    initialState.partnerAttributes = state.partnerAttributes;
  }

  async function saveTrayConfig(): Promise<boolean> {
    try {
      const payload = createTrayConfigPayload();
      const requestedEnabled = payload.config?.enabled;
      await updateTrayInstanceConfig(payload);
      syncInitialStateFromCurrent();
      await partnerCloudStore.refreshPartnerCloudStore();

      if (
        requestedEnabled !== undefined &&
        enabledTray.value?.enabled !== requestedEnabled
      ) {
        state.isEnabled = enabledTray.value?.enabled ?? false;
        initialState.isEnabled = state.isEnabled;
        const action = requestedEnabled ? 'enabled' : 'disabled';
        addErrorFlash({
          message: 'Failed to update data push status',
          description: `Your other settings were saved, but the integration could not be ${action}. Please try again or contact support.`,
        });
        return false;
      }

      return true;
    } catch (err) {
      addErrorFlash({
        message: 'Failed to save tray configuration',
      });
      captureException(err);
      return false;
    }
  }

  const partnersStore = usePartnersStore();

  const mapPartnersToFields = (field: SharedPartnerField) => {
    const { organization_uuid, nickname, source_field_id, column } = field;
    const partner = partnersStore.getPartnerOrgByUuid(organization_uuid);
    if (!partner) {
      return null;
    }
    return {
      ...partner,
      users: partner.users ?? [],
      tags: partner.tags ?? [],
      label: nickname ?? column,
      value: source_field_id,
    };
  };

  onBeforeMount(async () => {
    if (!integrationProfileId.value) return;

    const [sharedResponse, mappingResponse] = await Promise.all([
      crossbeamApi.GET(
        `/v0.1/integration-profiles/{profile_id}/shared-partner-fields`,
        {
          params: {
            path: {
              profile_id: integrationProfileId.value,
            },
          },
        },
      ),
      crossbeamApi.GET(
        `/v0.1/integration-profiles/{profile_id}/partner-field-mappings`,
        {
          params: {
            path: {
              profile_id: integrationProfileId.value,
            },
          },
        },
      ),
    ]);
    if (sharedResponse.data) {
      const mappedSharedFields = sharedResponse.data.items.reduce(
        (acc, curr) => {
          const mappedField = mapPartnersToFields(curr);
          if (mappedField) acc.push(mappedField);
          return acc;
        },
        [] as PartnerOption[],
      );
      const sortedSharedFields = sortBy(mappedSharedFields, ['name', 'label']); // sort by partner name then field label
      initialState.sharedPartnerFields = sortedSharedFields;
      state.sharedPartnerFields = sortedSharedFields;
    }

    const existingMappings = mappingResponse.data?.items;
    if (!existingMappings?.length) return;

    setPartnerFieldMappings(existingMappings);
  });

  async function saveTabSettings(activeTab: TabValue) {
    state.isSaving = true;

    let trayConfigSuccess = true;
    const promises: Promise<void>[] = [];

    if (state.hasTrayConfigChanges) {
      trayConfigSuccess = await saveTrayConfig();
    }

    if (activeTab === 'account-object' && isPartnerFieldMappingsDirty.value) {
      promises.push(savePartnerFieldMappings());
    }

    try {
      await Promise.all(promises);
    } catch (error) {
      captureException(error);
      addErrorFlash({
        message: 'Something went wrong',
        description: 'Failed to save settings',
      });
      state.isSaving = false;
      return;
    }

    if (trayConfigSuccess) {
      let toastMessage = '';
      switch (activeTab) {
        case 'account-object':
          toastMessage =
            integrationProfileType.value === 'salesforce'
              ? 'Account Object settings saved successfully'
              : 'Company Object settings saved successfully';
          break;
        case 'custom-object':
          toastMessage = 'Custom Object settings saved successfully';
          break;
        default:
          toastMessage = 'Settings saved successfully';
          break;
      }
      addSuccessFlash({
        message: toastMessage,
      });
    }

    state.isSaving = false;
  }

  return {
    integrationProfileType,
    isTrayConfigUpdateRequired: newIsTrayConfigUpdateRequired,
    enabledTray,
    isIntegrationEnabled,
    saveTrayConfig,
    savePartnerFieldMappings,
    state,
  };
}
